import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Search, Briefcase, Loader2 } from "lucide-react";
import JobCard from "../../Components/Graduado/JobCard";
import api from "../../API/api";
import { getJobs } from "../../API/getters";

export default function JobPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [allJobs, setAllJobs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedJobs = await getJobs();
      setAllJobs(fetchedJobs);
      setJobs(fetchedJobs);
    };
    fetchData();
  }, []);

  // Buscar no backend quando o termo muda
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      if (searchTerm.trim()) {
        fetchJobs(searchTerm);
      } else {
        // Volta a lista original quando o campo de pesquisa estiver vazio.
        setJobs(allJobs);
        setError(null);
      }
    }, 500); // Debounce de 500ms

    return () => clearTimeout(delayTimer);
  }, [searchTerm, allJobs]);

  const fetchJobs = async (query) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/vagas/search`, {
        params: { q: query },
      });
      setJobs(response.data);
    } catch (err) {
      console.error("Erro ao buscar vagas:", err);
      setError("Erro ao buscar vagas. Tente novamente.");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="w-full py-6">
      {/* Header Section */}
     
      {/* Search Bar */}
      <div className="max-w-2xl mx-auto mb-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Pesquisar vagas por título, empresa ou localização..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="
              w-full pl-12 pr-4 py-3
              bg-white border border-gray-200 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
              text-gray-700 placeholder:text-gray-400 text-sm
              transition-all duration-300
            "
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center py-16">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
          <span className="text-gray-400 text-sm">Carregando vagas...</span>
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="bg-red-50/50 rounded-xl p-6 border border-red-100 text-center max-w-2xl mx-auto">
          <div className="text-red-500 text-sm mb-2">{error}</div>
          <button 
            onClick={() => fetchJobs(searchTerm)}
            className="text-emerald-600 text-sm hover:text-emerald-700 inline-flex items-center gap-1"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Jobs List */}
      {!loading && jobs.length > 0 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              {jobs.length} {jobs.length === 1 ? "vaga encontrada" : "vagas encontradas"}
            </p>
          </div>
          {jobs.map((job) => (
            <JobCard
              key={job.uuid}
              title={job.title}
              company={job.company}
              location={job.location}
              onView={() => navigate(`/vagas/${job.uuid}`, { state: job })}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && searchTerm && jobs.length === 0 && (
        <div className="bg-gray-50/50 rounded-xl p-12 text-center border border-gray-100 max-w-2xl mx-auto">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
              <Briefcase className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <h3 className="text-lg font-light text-gray-700 mb-2">
            Nenhuma vaga encontrada
          </h3>
          <p className="text-gray-400 text-sm">
            Não encontramos resultados para "{searchTerm}". Tente outra palavra-chave.
          </p>
        </div>
      )}

      {/* Initial Empty State */}
      {!loading && !error && !searchTerm && jobs.length === 0 && (
        <div className="bg-gray-50/50 rounded-xl p-12 text-center border border-gray-100 max-w-2xl mx-auto">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
              <Briefcase className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <h3 className="text-lg font-light text-gray-700 mb-2">
            Nenhuma vaga disponível
          </h3>
          <p className="text-gray-400 text-sm">
              Novas oportunidades serão publicadas em breve.
          </p>
        </div>
      )}
    </div>
  );
}